import { Routes } from '@angular/router';
import { AuthSessionGuard } from '@core/guards/auth-session.guard';
import { ProcessRouteGuard } from '@core/guards/process-route.guard';
import {
  COMPLETED_STATUSES,
  DEPOSIT_STATUSES,
  DOCUMENT_UPLOAD_STATUSES,
  LEAD_FORM_STATUSES,
  SIGNING_STATUSES,
} from '@services/process-route/process-route.constants';

const PROCESS_ROUTE_GUARDS = [AuthSessionGuard, ProcessRouteGuard];
const PROCESS_AUTH_GUARDS = [AuthSessionGuard];

const routes_404_white_label: Routes = [
  {
    path: '',
    loadChildren: () => import('@white-label/routes/home/home.module').then((m) => m.HomeWhiteLabelModule),
  },
  {
    path: '404',
    loadChildren: () => import('@white-label/routes/pagina-404/pagina-404.module').then((m) => m.Pagina404Module),
  },
  {
    path: '**',
    redirectTo: '/404',
  },
];

const routes_app_white_label: Routes = [
  {
    path: 'legal/coltefinanciera',
    loadComponent: () =>
      import('@white-label/routes/terminos-legales/pages/terms-conditions.component').then(
        (m) => m.TermsConditionsWhiteLabel,
      ),
  },
];

const routes_tubo_white_label: Routes = [
  {
    path: 'login',
    loadChildren: () => import('@white-label/routes/login/login.module').then((m) => m.LoginModule),
  },
  /* Destino de `ProcessSessionService.buildLoginUrlTree()`, que apunta a `/portal/login` sin
     distinguir marca. Sin este redirect el white label mandaría al 404 al perder sesión. */
  {
    path: 'portal/login',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'mis-procesos',
    canActivate: PROCESS_AUTH_GUARDS,
    loadChildren: () => import('@white-label/routes/mis-procesos/mis-procesos.module').then((m) => m.MisProcesosModule),
  },
  {
    path: 'validar-identidad',
    canActivate: PROCESS_ROUTE_GUARDS,
    loadChildren: () =>
      import('@white-label/routes/validar-identidad/validar-identidad.module').then((m) => m.ValidarIdentidadModule),
    data: { requireActiveProcess: true, expectedStatuses: LEAD_FORM_STATUSES },
  },
  {
    path: 'confirmar-datos-truora',
    canActivate: PROCESS_ROUTE_GUARDS,
    loadChildren: () =>
      import('@white-label/routes/confirmar-datos-truora/confirmar-datos-truora.module').then(
        (m) => m.ConfirmarDatosTruoraModule,
      ),
    data: { requireActiveProcess: true, expectedStatuses: LEAD_FORM_STATUSES },
  },
  {
    path: 'v2/preguntas-cdturbo',
    canActivate: PROCESS_ROUTE_GUARDS,
    loadChildren: () =>
      import('@white-label/routes/preguntas-cdturbo/preguntas-cdturbo.module').then((m) => m.PreguntasCdturboModule),
    data: { requireActiveProcess: true, expectedStatuses: LEAD_FORM_STATUSES },
  },

  {
    path: 'consulta-en-listas',
    canActivate: PROCESS_AUTH_GUARDS,
    loadChildren: () =>
      import('@white-label/routes/consulta-en-listas/consulta-en-listas.module').then((m) => m.ConsultaEnListasModule),
  },
  {
    path: 'validacion-interna-proceso',
    canActivate: PROCESS_ROUTE_GUARDS,
    loadComponent: () =>
      import('../../../../routes/process-restrictions/internal-validation/internal-validation.component').then(
        (m) => m.InternalValidationComponent,
      ),
    data: {
      requireActiveProcess: true,
      allowedDecisionTypes: ['redirect-to-waiting'],
    },
  },
  {
    path: 'informacion-en-revision',
    canActivate: PROCESS_ROUTE_GUARDS,
    loadComponent: () =>
      import('../../../../routes/process-restrictions/restricted-information/restricted-information.component').then(
        (m) => m.RestrictedInformationComponent,
      ),
    data: {
      requireActiveProcess: true,
      allowedDecisionTypes: ['redirect-to-blocked'],
    },
  },
  {
    path: 'sube-tus-documentos',
    canActivate: PROCESS_ROUTE_GUARDS,
    loadChildren: () =>
      import('@white-label/routes/sube-tus-documentos/sube-tus-documentos.module').then(
        (m) => m.SubeTusDocumentosModule,
      ),
    data: { requireActiveProcess: true, expectedStatuses: DOCUMENT_UPLOAD_STATUSES },
  },
  {
    path: 'asegura-tu-tasa',
    canActivate: PROCESS_ROUTE_GUARDS,
    loadChildren: () =>
      import('@white-label/routes/asegura-tu-tasa/asegura-tu-tasa.module').then((m) => m.AseguraTuTasaModule),
    data: { requireActiveProcess: true, expectedStatuses: SIGNING_STATUSES },
  },

  {
    path: 'no-podemos-continuar-con-tu-proceso',
    canActivate: PROCESS_ROUTE_GUARDS,
    loadChildren: () =>
      import('@white-label/routes/no-podemos-continuar-con-tu-proceso/no-podemos-continuar-con-tu-proceso.module').then(
        (m) => m.NoPodemosContinuarConTuProcesoModule,
      ),
    data: {
      requireActiveProcess: true,
      allowedDecisionTypes: ['redirect-to-hard-block'],
    },
  },
  {
    path: 'firma-los-documentos-en-fisico',
    canActivate: PROCESS_AUTH_GUARDS,
    loadChildren: () =>
      import('@white-label/routes/no-podemos-continuar-con-tu-proceso/no-podemos-continuar-con-tu-proceso.module').then(
        (m) => m.NoPodemosContinuarConTuProcesoModule,
      ),
  },
  {
    path: 'bloqueo-temporal',
    canActivate: PROCESS_ROUTE_GUARDS,
    loadChildren: () =>
      import('@white-label/routes/bloqueo-temporal/bloqueo-temporal.module').then((m) => m.BloqueoTemporalModule),
    data: {
      requireActiveProcess: true,
      allowedDecisionTypes: ['redirect-to-temporary-block'],
    },
  },
  {
    path: 'te-contactaremos',
    canActivate: PROCESS_ROUTE_GUARDS,
    loadChildren: () =>
      import('@white-label/routes/te-contactaremos/te-contactaremos.module').then((m) => m.TeContactaremosModule),
    data: {
      requireActiveProcess: true,
      allowedDecisionTypes: ['redirect-to-contact'],
    },
  },
  {
    path: 'transfiere-el-dinero',
    canActivate: PROCESS_ROUTE_GUARDS,
    loadChildren: () =>
      import('@white-label/routes/transfiere-el-dinero/transfiere-el-dinero.module').then(
        (m) => m.TransfiereElDineroModule,
      ),
    data: { requireActiveProcess: true, expectedStatuses: DEPOSIT_STATUSES },
  },
  {
    path: 'cdt-completado',
    canActivate: PROCESS_ROUTE_GUARDS,
    loadChildren: () =>
      import('@white-label/routes/cdt-completado/cdt-completado.module').then((m) => m.CdtCompletadoModule),
    data: { requireActiveProcess: true, expectedStatuses: COMPLETED_STATUSES },
  },
];

export class routingColtefinanciera {
  /* White Label */
  public static routes: Routes = [...routes_app_white_label, ...routes_tubo_white_label, ...routes_404_white_label];
}
